import { ref } from 'vue'
import { isImpersonationAuthIsolated, supabase } from '@/supabase'
import { endImpersonation as apiEndImpersonation } from '@/api/lightbase.api'
import { IMPERSONATION_KEY } from '@/utils/impersonationHeaders'
import {
    readPersistedLocalSessionRaw,
    restorePersistedLocalSession,
} from '@/utils/supabaseLocalSession'
import { clearInboxCache } from './inbox/inboxCache'
import { getSupportAccess, getSupportSession } from '@/api/supportAccess.api'
import {
    clearSupportViewState,
    handoffAccessMode,
    resolveSupportViewMode,
    setSupportViewState,
    supportViewLimited,
    supportViewMode,
    supportViewUntil,
} from '@/utils/supportView'

// Impersonation is initiated from the Lightbase admin app (a separate origin).
// Lightbase mints a target Supabase session via the backend and hands it off to
// this product app at `/impersonate#access_token=...`. We consume those tokens
// here, swap the Supabase session, and remember enough to render the banner and
// restore state on exit.
//
// The impersonation tab uses a sessionStorage-backed Supabase client with its
// own storage key (see supabase.ts) so other product tabs — e.g. the dev
// harness — keep the staff session in localStorage.
//
// sessionStorage (not localStorage): the stashed prior session must survive a
// reload during impersonation but should NOT outlive the browser tab.
//
// Support view: a session is `limited` (no Gmail/Outlook-derived content; the
// backend enforces it) unless the customer approved email access (`full`). The
// mode comes with the handoff (`access_mode`) and is confirmed with
// GET /user/support-session; see src/utils/supportView.ts.
const ADMIN_SESSION_KEY = 'lh_admin_session'

export interface ImpersonationState {
    sessionId: string
    targetUserId: string
    targetEmail: string
    targetName: string
    startedAt: string
    // Where to send the staff user back to (the Lightbase support tab) on exit.
    returnUrl?: string
    // When true (or omitted, for older handoffs), client-only override treats
    // the target as onboarded so staff can land in the product workspace.
    // When false, keep their real onboarding state.
    forceOnboarded?: boolean
    // Live onboarding sandbox: same isolated session, different banner + return.
    sandbox?: boolean
}

const impersonation = ref<ImpersonationState | null>(readImpersonationState())
let confirmInflight: Promise<void> | null = null

/**
 * Ask the backend which mode this session is in (it wins over the handoff), and for full access until when
 * (the customer's grant). Best-effort: without an answer the tab keeps the mode it has.
 */
function confirmSupportView(): Promise<void> {
    if (!impersonation.value) return Promise.resolve()
    if (confirmInflight) return confirmInflight
    confirmInflight = (async () => {
        const server = await getSupportSession().catch(() => null)
        if (!impersonation.value) return
        const mode = resolveSupportViewMode(supportViewMode.value, server)
        let until = server?.until ?? (mode === 'full' ? supportViewUntil.value : null)
        if (mode === 'full' && !until) {
            const access = await getSupportAccess().catch(() => null)
            if (!impersonation.value) return
            until = access?.active_grant?.granted_until ?? null
        }
        setSupportViewState({ mode, until })
    })().finally(() => {
        confirmInflight = null
    })
    return confirmInflight
}

function readImpersonationState(): ImpersonationState | null {
    try {
        const raw = sessionStorage.getItem(IMPERSONATION_KEY)
        return raw ? (JSON.parse(raw) as ImpersonationState) : null
    } catch {
        return null
    }
}

function writeImpersonationState(state: ImpersonationState | null) {
    impersonation.value = state
    if (state) {
        sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state))
    } else {
        sessionStorage.removeItem(IMPERSONATION_KEY)
    }
}

export default function useImpersonation() {
    const isImpersonating = () => impersonation.value !== null

    /**
     * Consume an impersonation handoff from the URL fragment (set by Lightbase).
     * Returns true if a handoff was processed (caller should redirect).
     */
    async function consumeHandoffFromUrl(): Promise<boolean> {
        const hash = window.location.hash.startsWith('#')
            ? window.location.hash.slice(1)
            : ''
        if (!hash) return false

        const params = new URLSearchParams(hash)
        const accessToken = params.get('access_token')
        const refreshToken = params.get('refresh_token')
        const sessionId = params.get('session_id')
        if (!accessToken || !refreshToken || !sessionId) return false

        // SPA navigation onto `/impersonate` would reuse the shared localStorage
        // client. Reload so this tab isolates auth before we call setSession.
        if (!isImpersonationAuthIsolated) {
            window.location.reload()
            await new Promise<never>(() => {})
        }

        const targetEmail = params.get('email') || ''
        const targetName = params.get('name') || targetEmail
        const returnUrl = params.get('return') || undefined
        // Default true so older Lightbase handoffs (no flag) keep skipping onboarding.
        const forceOnboarded = params.get('force_onboarded') !== '0'
        const sandbox = params.get('sandbox') === '1'

        // Read the staff session from localStorage — this tab's Supabase client
        // is isolated and will not see it via getSession().
        const existingRaw = readPersistedLocalSessionRaw()
        if (existingRaw) {
            sessionStorage.setItem(ADMIN_SESSION_KEY, existingRaw)
        } else {
            sessionStorage.removeItem(ADMIN_SESSION_KEY)
        }

        const { data: setData, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
        })
        if (error) {
            return false
        }

        const resolvedUserId = setData?.user?.id || ''
        // The staff member's own cached mail must not be readable from inside
        // the support session (the cache is keyed by user, but this is cheap
        // insurance against a key-scoping mistake).
        await clearInboxCache()
        writeImpersonationState({
            sessionId,
            targetUserId: resolvedUserId,
            targetEmail: targetEmail || setData?.user?.email || '',
            targetName,
            startedAt: new Date().toISOString(),
            returnUrl,
            forceOnboarded,
            sandbox,
        })
        // Hide email from the first paint when the handoff says so; the backend confirms in the background.
        setSupportViewState({ mode: handoffAccessMode(params), until: params.get('access_until') || null })
        void confirmSupportView()

        return true
    }

    async function exitImpersonating(): Promise<void> {
        const current = impersonation.value
        const stashRaw = sessionStorage.getItem(ADMIN_SESSION_KEY)

        // Best-effort audit close while still on the target session (the backend
        // authorizes the target to end its own impersonation session).
        if (current?.sessionId) {
            try {
                await apiEndImpersonation(current.sessionId)
            } catch {
                // Non-fatal.
            }
        }

        const returnUrl = current?.returnUrl
        await clearInboxCache()
        writeImpersonationState(null)
        clearSupportViewState()
        sessionStorage.removeItem(ADMIN_SESSION_KEY)

        // Drop only this tab's impersonation session. A global signOut would
        // revoke the target user's refresh token for their real sessions.
        try {
            await supabase.auth.signOut({ scope: 'local' })
        } catch {
            // Best-effort; sessionStorage is cleared by the reload below.
        }

        if (stashRaw) {
            // Another product tab may have kept/refreshed the staff session;
            // only write the stash back if that slot is empty.
            restorePersistedLocalSession(stashRaw)
            window.location.href = '/dashboard'
            return
        }

        // No prior product session: return to Lightbase.
        if (returnUrl) {
            window.location.href = returnUrl
        } else {
            window.location.href = '/'
        }
    }

    // Drop stale impersonation state when the Supabase session is gone (e.g. the
    // target session expired and could not refresh). Called on app boot.
    async function reconcileImpersonation(): Promise<boolean> {
        if (!impersonation.value) return false
        const { data } = await supabase.auth.getSession()
        if (!data?.session) {
            writeImpersonationState(null)
            clearSupportViewState()
            sessionStorage.removeItem(ADMIN_SESSION_KEY)
            return true
        }
        void confirmSupportView()
        return false
    }

    function isSandboxWorkspaceView() {
        const current = impersonation.value
        return Boolean(current?.sandbox && current.forceOnboarded)
    }

    return {
        impersonation,
        isImpersonating,
        consumeHandoffFromUrl,
        exitImpersonating,
        reconcileImpersonation,
        isSandboxWorkspaceView,
        /** 'limited' (support view, email hidden), 'full' (customer approved email access) or null (no restriction). */
        supportViewMode,
        /** True while email content is hidden from this tab. */
        supportViewLimited,
        /** When full access ends (ISO), when known. */
        supportViewUntil,
        confirmSupportView,
    }
}
